import {useHttpCalls} from '../api/useHttpCalls';
import {
  EagGridStore,
  selectActivePreset,
  selectActivePresetDataQuery,
  selectCustomPresets,
  selectIsPresetChanged,
  selectVirtualPreset,
} from '../store/createEagGridStore';
import {useEagGridSelector} from '../store/useEagGridSelector';
import {useEagGridStore} from '../store/useEagGridStore';
import {UsePresetStateReturn} from '../types/UsePresetStateReturn';

type UsePresetListReturn = Pick<
  UsePresetStateReturn,
  | 'presets'
  | 'virtualPreset'
  | 'activePreset'
  | 'activePresetDataQuery'
  | 'isPresetChanged'
  | 'setActivePreset'
  | 'setIsPresetChanged'
  | 'rowCountGetter'
> & {refreshPresets: () => ReturnType<ReturnType<typeof useHttpCalls>['getPresets']>};

/** Standalone so usePresetCrud can reuse it without prop-drilling a bound hook fn. */
export const refreshPresets = (httpCalls: ReturnType<typeof useHttpCalls>, store: EagGridStore) =>
  httpCalls.getPresets().then((response) => {
    if (response) {
      store.setCustomPresets(response);
    }
    return response;
  });

export const usePresetList = (): UsePresetListReturn => {
  const httpCalls = useHttpCalls();
  const store = useEagGridStore();

  const activePreset = useEagGridSelector(selectActivePreset)!;
  const customPresets = useEagGridSelector(selectCustomPresets) ?? [];
  const virtualPreset = useEagGridSelector(selectVirtualPreset);
  const activePresetDataQuery = useEagGridSelector(selectActivePresetDataQuery) ?? null;
  const isPresetChanged = useEagGridSelector(selectIsPresetChanged);

  const rowCountGetter = (dataQueryId: string): Promise<number> =>
    httpCalls.getCountByQuery(dataQueryId).then((response) => response ?? 0);

  return {
    presets: customPresets,
    virtualPreset,
    activePreset,
    activePresetDataQuery,
    isPresetChanged,
    setActivePreset: store.setActivePreset,
    setIsPresetChanged: store.setIsPresetChanged,
    rowCountGetter,
    refreshPresets: () => refreshPresets(httpCalls, store),
  };
};
